// ============================================================
// CLUBHUB (aus dem Agent) – Die Mahnstaffel des Vereins
// ============================================================
// Der Teil von `lib/settings/dunning.ts` aus dem Agent, den der
// Mahnstand braucht: Stufen mit Titel, Kuerzel, Frist, Gebuehr und
// Zusatz, dazu Karenz und Zinsen. Rein und ohne Datenbank.
//
// Abweichung: Die Staffel kommt aus den Einstellungen des Vereins
// (Finanzen › Rechnungen und Mahnwesen, `Club.settings`):
//   finanzen.mahnstufen     Zahl der Mahnungen NACH der Erinnerung (0–3, Vorgabe 2)
//   finanzen.mahnabstand_tage  Frist je Schreiben (Vorgabe 14)
//   finanzen.mahngebuehr    Gebuehr ab der ersten Mahnung in Euro (Vorgabe 0)
// Die Zahlungserinnerung kostet nie etwas; die letzte Mahnung traegt
// den Zusatz mit den Folgen nach der Satzung. Verzugszinsen nimmt ein
// Verein in aller Regel nicht — Vorgabe 0 %.
// ============================================================

export type DunningLevel = {
  level: number;
  title: string;
  /** Das Kuerzel fuer enge Stellen — „ZE“, „1. M“, „2. M“. */
  short: string;
  /** Tage bis zur neuen Frist, ab dem Tag des Schreibens. */
  days: number;
  feeCents: number;
  /** Der Absatz, den NUR diese Stufe druckt — leer = keiner. */
  notice: string;
};

export type DunningRules = {
  levels: DunningLevel[];
  /** Verzugszinsen in Prozent pro Jahr. */
  interestPercent: number;
  /** Tage nach der Faelligkeit, bevor die erste Stufe faellig wird. */
  graceDays: number;
};

/** Was die letzte Mahnung ankuendigt — angedroht wird nur, was der Vorstand auch tun kann. */
export const LETZTE_MAHNUNG_ZUSATZ =
  "Geht der Betrag nicht bis zum {Frist} ein, entscheidet der Vorstand über das weitere Vorgehen. Nach der Satzung kann das bis zum Ruhen der Mitgliedsrechte oder zur Streichung aus der Mitgliederliste führen.";

function zahl(wert: unknown, vorgabe: number, min: number, max: number): number {
  const n = typeof wert === "number" ? wert : typeof wert === "string" && wert.trim() ? Number(wert) : NaN;
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : vorgabe;
}

/** Die Staffel aus den Einstellungen des Vereins (`Club.settings`). */
export function vereinsMahnregeln(settings: Record<string, unknown> | null | undefined): DunningRules {
  const s = settings ?? {};
  const mahnungen = Math.trunc(zahl(s["finanzen.mahnstufen"], 2, 0, 3));
  const abstand = Math.trunc(zahl(s["finanzen.mahnabstand_tage"], 14, 7, 60));
  const gebuehrCents = Math.round(zahl(s["finanzen.mahngebuehr"], 0, 0, 5000) * 100);

  const levels: DunningLevel[] = [{ level: 1, title: "Zahlungserinnerung", short: "ZE", days: abstand, feeCents: 0, notice: "" }];
  for (let i = 1; i <= mahnungen; i++) {
    const letzte = i === mahnungen;
    levels.push({
      level: i + 1,
      title: letzte && mahnungen > 1 ? `${i}. und letzte Mahnung` : letzte ? "Letzte Mahnung" : `${i}. Mahnung`,
      short: `${i}. M`,
      days: abstand,
      feeCents: gebuehrCents,
      notice: letzte ? LETZTE_MAHNUNG_ZUSATZ : "",
    });
  }
  return { levels, interestPercent: zahl(s["finanzen.verzugszins_prozent"], 0, 0, 20), graceDays: 3 };
}

/** Verzugszinsen auf den offenen Betrag, taggenau (365 Tage, wie im Agent). */
export function interestCents(openCents: number, interestPercent: number, overdueSince: Date, today = new Date()): number {
  const tage = Math.max(0, Math.floor((today.getTime() - overdueSince.getTime()) / (24 * 60 * 60 * 1000)));
  if (tage === 0 || interestPercent <= 0) return 0;
  return Math.round((openCents * interestPercent * tage) / (100 * 365));
}
